import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import AddressAutocomplete from './AddressAutocomplete';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from './ui/select';

const INITIAL_FORM = {
  name: '',
  address: '',
  description: '',
  bedrooms: 0,
  bathrooms: 0,
  property_type: '',
  // Google place_id resolved from the address autocomplete (optional).
  place_id: '',
};

// Radix Select does not allow an empty-string item value, so we use a
// sentinel for the "No type" option and translate it back to '' in state.
const NO_TYPE_VALUE = '__none__';

const PropertyModal = ({
  isOpen,
  onClose,
  onSave,
  property,
  saving,
  saveError,
  onClearSaveError,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  // When the address is entered manually (no Google place_id), we ask the user
  // to confirm before saving since the address cannot be verified.
  const [showUnverifiedConfirm, setShowUnverifiedConfirm] = useState(false);

  const isEditing = !!property;

  useEffect(() => {
    if (property) {
      setFormData({
        name: property.name || '',
        address: property.address || '',
        description: property.description || '',
        bedrooms: property.bedrooms ?? 0,
        bathrooms: property.bathrooms ?? 0,
        property_type: property.property_type || '',
        place_id: property.google_place_id || '',
      });
    } else {
      setFormData(INITIAL_FORM);
    }
    setShowUnverifiedConfirm(false);
  }, [property, isOpen]);

  const persist = () => {
    onSave({
      name: formData.name,
      address: formData.address,
      description: formData.description,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms,
      property_type: formData.property_type,
      place_id: formData.place_id,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // No place_id means the address was typed manually and was not selected
    // from Google autocomplete, so it cannot be verified. Confirm first.
    if (!formData.place_id) {
      setShowUnverifiedConfirm(true);
      return;
    }
    persist();
  };

  const handleConfirmUnverified = () => {
    setShowUnverifiedConfirm(false);
    persist();
  };

  const update = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (saveError && onClearSaveError) onClearSaveError();
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Property' : 'Add New Property'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the details for this property.'
              : 'Fill in the details to add a new property.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-2">
          {saveError && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{saveError}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="prop-name">Property Name</Label>
            <Input
              id="prop-name"
              required
              value={formData.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g., Sunset Villa"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prop-address">Address</Label>
            <AddressAutocomplete
              id="prop-address"
              required
              value={formData.address}
              placeholder="e.g., 123 Main St, Ames, IA 50014"
              onChange={(text) => {
                // Manual typing invalidates any previously selected place.
                setFormData((prev) => ({ ...prev, address: text, place_id: '' }));
                if (saveError && onClearSaveError) onClearSaveError();
              }}
              onPlaceSelect={({ placeId, address }) => {
                setFormData((prev) => ({
                  ...prev,
                  address,
                  place_id: placeId || '',
                }));
                if (saveError && onClearSaveError) onClearSaveError();
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prop-description">Description</Label>
            <Input
              id="prop-description"
              value={formData.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Brief description of the property"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="prop-beds">Bedrooms</Label>
              <Input
                id="prop-beds"
                type="number"
                min="0"
                value={formData.bedrooms}
                onChange={(e) => update('bedrooms', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prop-baths">Bathrooms</Label>
              <Input
                id="prop-baths"
                type="number"
                min="0"
                value={formData.bathrooms}
                onChange={(e) => update('bathrooms', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prop-type">Type</Label>
              <Select
                value={formData.property_type || NO_TYPE_VALUE}
                onValueChange={(val) =>
                  update('property_type', val === NO_TYPE_VALUE ? '' : val)
                }
              >
                <SelectTrigger id="prop-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_TYPE_VALUE}>None</SelectItem>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="house">House</SelectItem>
                  <SelectItem value="ranch">Ranch</SelectItem>
                  <SelectItem value="condo">Condo</SelectItem>
                  <SelectItem value="townhouse">Townhouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#16A34A] hover:bg-[#15803D] text-white"
            >
              {saving
                ? 'Saving…'
                : isEditing
                ? 'Save Changes'
                : 'Add Property'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <Dialog
      open={showUnverifiedConfirm}
      onOpenChange={(open) => !open && setShowUnverifiedConfirm(false)}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add address without verification?</DialogTitle>
          <DialogDescription>
            This address was entered manually and was not selected from the
            suggestions, so it can't be verified. You can still
            {isEditing ? ' save' : ' add'} the property, but it will be marked as
            unverified.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="break-words">{formData.address || 'No address entered'}</span>
        </div>

        <DialogFooter className="gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowUnverifiedConfirm(false)}
          >
            Go back
          </Button>
          <Button
            type="button"
            onClick={handleConfirmUnverified}
            disabled={saving}
            className="bg-[#16A34A] hover:bg-[#15803D] text-white"
          >
            {saving
              ? 'Saving…'
              : isEditing
              ? 'Save without verification'
              : 'Add without verification'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default PropertyModal;
